/**
 * The 2g layout as the client renderers draw it (canvas, block card, the tables
 * page's previews): a cell's note mark, the notes under the table, and the
 * group header row. One place, so every renderer draws them alike.
 */
import type { CSSProperties } from "react";
import { testIds } from "@/testIds";
import { renderMarkersToHtml } from "@/lib/render-markers";
import { groupRowCells, type HeaderGroup, type TableNote } from "./tableLayout";

const markStyle: CSSProperties = { fontSize: "0.72em", marginLeft: 1, fontWeight: 600 };

/** A cell's note mark, as it prints: a superscript letter after the text. */
export function NoteMark({ mark }: { mark: string | null | undefined }) {
  if (!mark) return null;
  return <sup data-testid={testIds.tableTool.noteMark} data-mark={mark} style={markStyle}>{mark}</sup>;
}

/** The same mark as HTML, for the renderers that draw a cell from its marker HTML. Marks are a–z only. */
export const noteMarkHtml = (mark: string | null | undefined): string =>
  mark && /^[a-z]+$/.test(mark)
    ? `<sup data-testid="${testIds.tableTool.noteMark}" data-mark="${mark}" style="font-size:0.72em;margin-left:1px;font-weight:600">${mark}</sup>`
    : "";

/** The notes under the table, each after its mark, smaller than the table — as threeparttable prints them. */
export function TableNotes({ notes, style }: { notes: readonly TableNote[]; style?: CSSProperties }) {
  if (!notes.length) return null;
  return (
    <ol data-testid={testIds.tableTool.notes} style={{ listStyle: "none", margin: "4px 0 0", padding: 0, fontSize: "0.82em", lineHeight: 1.45, ...style }}>
      {notes.map((n) => (
        <li key={n.mark} data-testid={testIds.tableTool.note} data-mark={n.mark}>
          <sup style={{ marginRight: 3 }}>{n.mark}</sup>
          <span dangerouslySetInnerHTML={{ __html: renderMarkersToHtml(n.text) }} />
        </li>
      ))}
    </ol>
  );
}

/**
 * The group header row: each label centred over its columns with a short rule
 * under it (the trimmed \cmidrule), blanks elsewhere. `cellStyle` is the
 * renderer's header cell style.
 */
export function GroupHeaderRow({ groups, width, cellStyle, ruleColor = "currentColor" }: {
  groups: readonly HeaderGroup[];
  width: number;
  cellStyle?: CSSProperties;
  ruleColor?: string;
}) {
  if (!groups.length) return null;
  return (
    <tr data-testid={testIds.tableTool.groupRow} data-part="group-head">
      {groupRowCells(groups, width).map((g) =>
        g.label === null ? (
          <th key={`blank-${g.start}`} aria-hidden style={{ ...cellStyle, borderBottom: undefined, borderTop: cellStyle?.borderTop }} />
        ) : (
          <th
            key={`group-${g.start}`}
            scope="colgroup"
            colSpan={g.span > 1 ? g.span : undefined}
            data-testid={testIds.tableTool.groupLabel}
            data-start={g.start}
            data-span={g.span}
            style={{ ...cellStyle, textAlign: "center", borderBottom: undefined, paddingBottom: 2 }}
          >
            <span dangerouslySetInnerHTML={{ __html: renderMarkersToHtml(g.label) }} />
            {/* The trimmed rule: short of the cell's edges on both sides, as \cmidrule(lr). */}
            <span aria-hidden style={{ display: "block", height: 0, borderBottom: `0.75px solid ${ruleColor}`, margin: "3px 4px 0" }} />
          </th>
        ),
      )}
    </tr>
  );
}
